// src/features/marketResearch/components/sections/MarketOverview.tsx
import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Box,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  ShowChart as ShowChartIcon,
  TrendingUp,
  Business,
  Assessment,
  Timeline,
  AttachMoney
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { SectionContainer, SectionTitle, SectionBody, SubSectionTitle, MetricHighlight } from '../Section';
import { MarketOverviewData } from '../../types';

interface Props {
  data?: MarketOverviewData;
}

export const MarketOverview: React.FC<Props> = ({ data = {} }) => {
  const theme = useTheme();
  const {
    marketSize,
    growthRate,
    demandDrivers = [],
    seasonalityData = []
  } = data;

  // Generate sample seasonality data if none provided
  const defaultSeasonalityData = [
    { month: 'Jan', value: 85, occupancy: 78 },
    { month: 'Feb', value: 88, occupancy: 82 },
    { month: 'Mar', value: 92, occupancy: 85 },
    { month: 'Apr', value: 95, occupancy: 88 },
    { month: 'May', value: 98, occupancy: 92 },
    { month: 'Jun', value: 100, occupancy: 95 },
    { month: 'Jul', value: 100, occupancy: 96 },
    { month: 'Aug', value: 99, occupancy: 94 },
    { month: 'Sep', value: 94, occupancy: 89 },
    { month: 'Oct', value: 90, occupancy: 85 },
    { month: 'Nov', value: 87, occupancy: 80 },
    { month: 'Dec', value: 89, occupancy: 83 }
  ];

  const chartData = seasonalityData.length > 0 ? seasonalityData : defaultSeasonalityData;

  // Market composition data for pie chart
  const marketComposition = [
    { name: 'Business', value: 45, color: '#1976d2' },
    { name: 'Leisure', value: 35, color: '#388e3c' },
    { name: 'Groups', value: 12, color: '#f57c00' },
    { name: 'Other', value: 8, color: '#7b1fa2' }
  ];

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  return (
    <SectionContainer id="market-overview">
      <SectionTitle>Market Overview & Analysis</SectionTitle>
      <SectionBody>
        {/* Key Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                color: 'white',
                '@media print': { background: '#1976d2' }
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Business sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {marketSize ? formatCurrency(marketSize) : 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Market Size
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)',
                color: 'white',
                '@media print': { background: '#388e3c' }
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {growthRate ? `${growthRate.toFixed(1)}%` : 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Annual Growth Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
                color: 'white',
                '@media print': { background: '#f57c00' }
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Assessment sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {demandDrivers.length}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Key Demand Drivers
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)',
                color: 'white',
                '@media print': { background: '#7b1fa2' }
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Timeline sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  96%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Peak Occupancy
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Seasonality Chart */}
          <Grid item xs={12} lg={8}>
            <Paper
              sx={{
                p: 3,
                height: 380,
                '@media print': { height: 300 }
              }}
            >
              <SubSectionTitle>Seasonal Demand Patterns</SubSectionTitle>
              <Box className="chart-container" sx={{ height: 280, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12 }}
                      stroke="#666"
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      stroke="#666"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#1976d2"
                      strokeWidth={3}
                      dot={{ fill: '#1976d2', strokeWidth: 2, r: 5 }}
                      name="Demand Index"
                    />
                    <Line
                      type="monotone"
                      dataKey="occupancy"
                      stroke="#388e3c"
                      strokeWidth={3}
                      dot={{ fill: '#388e3c', strokeWidth: 2, r: 5 }}
                      name="Occupancy %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Market Composition */}
          <Grid item xs={12} lg={4}>
            <Paper
              sx={{
                p: 3,
                height: 380,
                '@media print': { height: 300 }
              }}
            >
              <SubSectionTitle>Market Composition</SubSectionTitle>
              <Box className="chart-container" sx={{ height: 200, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={marketComposition}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {marketComposition.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ mt: 2 }}>
                {marketComposition.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: item.color,
                        borderRadius: 1,
                        mr: 1
                      }}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Demand Drivers */}
        <Paper sx={{ p: 3 }}>
          <SubSectionTitle>Primary Demand Drivers</SubSectionTitle>
          <Grid container spacing={2}>
            {demandDrivers.length > 0 ? (
              demandDrivers.map((driver, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    <ShowChartIcon
                      sx={{
                        color: '#1976d2',
                        mr: 2,
                        fontSize: 24
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#37474f'
                      }}
                    >
                      {driver}
                    </Typography>
                  </Box>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No specific demand drivers identified in the current analysis.
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Market Risk Assessment */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
              Market Risk Assessment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <MetricHighlight label="Market Risk" value="Low" color="#388e3c" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MetricHighlight label="Volatility" value="Moderate" color="#f57c00" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MetricHighlight label="Outlook" value="Positive" color="#1976d2" />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </SectionBody>
    </SectionContainer>
  );
};
